/**
 * Shared User (SSO) filter helpers used by Analytics, ReachOut, and Stack Ranking.
 * Filter value is always the SSO email; display value is the recruiter name.
 */

export function normalizeSsoEmail(email) {
  return String(email || "").trim().toLowerCase();
}

function recordSsoEmail(record) {
  return normalizeSsoEmail(
    record?.createdByEmail || record?.uploadedByEmail || record?.recruiterEmail || ""
  );
}

function recordSsoName(record) {
  return (record?.createdBy || record?.uploadedBy || record?.createdByName || record?.recruiterName || "").trim();
}

export function positionSsoId(pos) {
  return pos?.positionId ?? pos?.id ?? pos?.position_id ?? null;
}

/** SSO owner of a job — same identity Analytics uses for User (SSO). */
export function positionSsoRecruiter(pos) {
  return {
    createdBy: recordSsoName(pos),
    createdByEmail: recordSsoEmail(pos),
  };
}

export function uniqueSsoRecruiters(records) {
  const map = new Map();
  (records || []).forEach((p) => {
    const email = recordSsoEmail(p);
    const name = recordSsoName(p) || email;
    if (email && !map.has(email)) map.set(email, name);
  });
  return Array.from(map.entries())
    .map(([email, name]) => ({ email, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * User (SSO) names from tenant membership, not job creators.
 * Empty selected tenants = all members in the payload (already reachable-only).
 * A tenant with nobody added returns an empty list — never sibling-tenant names.
 */
export function uniqueTenantMembers(members, selectedTenantIds) {
  const wanted =
    selectedTenantIds && selectedTenantIds.size
      ? new Set(Array.from(selectedTenantIds).map((id) => String(id)))
      : null;
  const map = new Map();
  (members || []).forEach((m) => {
    const tenantId = m?.tenantId ?? m?.tenant_id ?? m?.tenant?.id;
    if (wanted && (tenantId == null || tenantId === "" || !wanted.has(String(tenantId)))) {
      return;
    }
    const email = normalizeSsoEmail(m?.email);
    if (!email || map.has(email)) return;
    const name = String(m?.name || "").trim() || email;
    map.set(email, { email, name });
  });
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
}

/** Recruiters who attached resumes to this position — not org-wide job owners. */
export function uniqueResumeAttachers(resumes) {
  return uniqueSsoRecruiters(
    (resumes || []).map((r) => ({
      uploadedBy: r?.uploadedBy,
      uploadedByEmail: r?.uploadedByEmail,
    }))
  );
}

export function matchesSsoRecruiter(record, selectedEmail) {
  if (!selectedEmail) return true;
  return recordSsoEmail(record) === normalizeSsoEmail(selectedEmail);
}

/** Empty set = All recruiters (same as Analytics Recruiter SSO). */
export function matchesSsoRecruiterSet(record, selectedEmails) {
  if (!selectedEmails || selectedEmails.size === 0) return true;
  const email = recordSsoEmail(record);
  const selected = new Set(Array.from(selectedEmails).map(normalizeSsoEmail));
  return selected.has(email);
}

function selectedEmailSet(selectedEmails) {
  return new Set(Array.from(selectedEmails || []).map(normalizeSsoEmail));
}

/** Positions created by the selected SSO recruiter(s) — same scope as Analytics. */
export function positionsForSsoRecruiters(positions, selectedEmails) {
  if (!selectedEmails || selectedEmails.size === 0) return positions || [];
  const selected = selectedEmailSet(selectedEmails);
  return (positions || []).filter((p) => selected.has(recordSsoEmail(p)));
}

export function positionTenantId(pos) {
  const raw = pos?.tenantId ?? pos?.tenant?.id ?? pos?.tenant_id;
  return raw == null || raw === "" ? "" : String(raw);
}

/** Empty set = all tenants (parent + children). */
export function positionsForTenants(positions, selectedTenantIds) {
  if (!selectedTenantIds || selectedTenantIds.size === 0) return positions || [];
  const wanted = new Set(Array.from(selectedTenantIds).map((id) => String(id)));
  return (positions || []).filter((p) => wanted.has(positionTenantId(p)));
}

/**
 * Stamp Attached By from resumes linked to THIS position only.
 * Candidates whose resume is not in the position set are dropped so the
 * User (SSO) filter cannot surface rankings from other jobs.
 */
export function attachResumeCreatedBy(candidates, resumes, positionFallback) {
  const byId = new Map();
  const positionResumeIds = new Set();
  (resumes || []).forEach((r) => {
    const id = r.resumeId ?? r.resume_id;
    if (id == null) return;
    const key = String(id);
    positionResumeIds.add(key);
    const email = (r.uploadedByEmail || r.uploaded_by_email || "").trim();
    const name = (r.uploadedBy || r.uploaded_by || email).trim();
    byId.set(key, { createdBy: name, createdByEmail: email });
  });
  const scoped = (candidates || []).filter((c) => {
    if (positionResumeIds.size === 0) return false;
    const id = c.resume_id ?? c.resumeId;
    return id != null && positionResumeIds.has(String(id));
  });
  return scoped.map((c) => {
    const hit = byId.get(String(c.resume_id ?? c.resumeId ?? ""));
    const createdByEmail = (hit?.createdByEmail || c.uploadedByEmail || "").trim();
    const createdBy = (hit?.createdBy || c.uploadedBy || createdByEmail || "").trim();
    return {
      ...c,
      createdBy: createdBy || "—",
      createdByEmail,
      attachedBy: createdBy || "—",
      attachedByEmail: createdByEmail,
      positionId: positionSsoId(positionFallback) ?? c.positionId,
      jobRole: positionFallback?.positionName || positionFallback?.title || c.jobRole || "",
    };
  });
}
